import {ESerialMode, Serializable} from "lib/commons/io/serial";
import {DOM, ENodeType} from "lib/commons/xml/dom";
import {IJmlObj, IJmlSet, JML} from "lib/commons/xml/jml";
import {XA} from "lib/commons/xml/xAddr";
import {XmlBodyState, XmlDeleteMsg, XmlInsertMsg, XmlListMsgOt, XmlStrMsg} from "lib/edit/ot/xmlHouse";
import {ISkAnnot, ISkStructDef} from "lib/edit/schema/schemaAnnots";
import {ISkPatternResolver} from "lib/edit/schema/schemaBuilder";
import {ISkImporter, OSkImportCtxInternal, OSkImportDatas, OSkMCopyScope, OSkPasteContext, OSkPasteSearchContext, SKMETA} from "lib/edit/schema/schemaMeta";
import {initSkRuleDefaults, ISkRuleObj, SkRule, SkRuleAttr, SkRuleAttrUnknown, SkRuleChoice, SkRuleComment, SkRuleDirective, SkRuleEltUnknown, SkRuleGroup, SkRuleNode, SkRuleTextForbidden} from "lib/edit/schema/schemaPatterns";
import {MsgOt} from "lib/edit/ot/houseOt";
import {IGram, IGramTransposer} from "lib/edit/import/gram";
import {ISchemaSearch, ISkSearchAnnot} from "lib/edit/schema/schemaSearch";
import {XmlTypedHouse} from "lib/edit/ot/xmlTypedHouse";
import {ISkDiffAnnot, IXmlDiffSession} from "lib/edit/schema/diff";

/** Associe à un objet (un Msg) des annotations à ajouter ou supprimer. */
export interface ISkAnnotated {
	skAnnotsToAdd?: ISkAnnot[];
	skAnnotsToRemove?: ISkAnnot[];
}

export function isSkAnnotated(obj: any): obj is ISkAnnotated {return obj && (obj.skAnnotsToAdd || obj.skAnnotsToRemove)}


export interface OSchemaConfig {
	unknownEltRule?: SkRule;
	unknownAttrRule?: SkRule;
	forbiddenTextRule?: SkRule;
	commentRule?: SkRuleComment;
	namespaces?: Dict<string>;
	crossDomMgrFactories?: Dict<(schemaDom: SchemaDom) => ISkCrossDomMgr>;
}

/** Gestionnaire transversal attaché à un SchemaDom d'un document (id...). */
export interface ISkCrossDomMgr {

	onStartValid?(options: OSkExecInternalOptions): void

	onEndValid?(options: OSkExecInternalOptions): void
}

/**
 * Schéma "compilé" prêt à être appliqué sur des DomNodes.
 */
export class Schema extends Serializable implements ISkPatternResolver {


	static buildFromJson(json: any): any {
		return new Schema().readFrom(json, ESerialMode.json);
	}

	static buildFromStruct(struct: any): any {
		return new Schema().readFrom(struct, ESerialMode.struct);
	}

	startRule: SkRuleNode | SkRuleDirective;
	unknownEltRule: SkRule;
	unknownAttrRule: SkRule;
	forbiddenTextRule: SkRule;
	commentRule: SkRule;

	namespaces: Dict<string> | null;

	crossDomMgrFactories?: Dict<(schemaDom: SchemaDom) => ISkCrossDomMgr>;

	/**
	 *
	 * @param startRule Généralement un SkRuleDoc, SkRuleElt ou une SkRuleDirective pour proposer un choix.
	 * @param options
	 */
	constructor(startRule?: SkRuleNode | SkRuleDirective, options: OSchemaConfig = {}) {
		super();
		initSkRuleDefaults();
		this.startRule = startRule;
		this.unknownEltRule = options.unknownEltRule || SkRuleEltUnknown.DEFAULT;
		this.unknownAttrRule = options.unknownAttrRule || SkRuleAttrUnknown.DEFAULT;
		this.forbiddenTextRule = options.forbiddenTextRule || SkRuleTextForbidden.DEFAULT;
		this.commentRule = options.commentRule || SkRuleComment.DEFAULT;
		this.namespaces = options.namespaces;
		this.crossDomMgrFactories = options.crossDomMgrFactories;
	}

	/**
	 * Association initiale de ce schema avec un noeud DOM racine (généralement un Document).
	 * Aucune validation n'est effectuée à ce stade : voir les méthodes de SchemaDom.
	 *
	 * @return SchemaDom ou null si aucune association avec domNode n'a pu être trouvée
	 *  (uniquement dans le cas où on n'est pas au niveau Document, mais en validation partielle d'un document
	 *  exigeant de matcher un élément particulier).
	 */
	bindToDom(domNode: Document | DocumentFragment | Element, options?: OSchemaDomConfig): SchemaDom {
		const ruleNode = options && options.rootRule ? options.rootRule : this.startRule.findRuleNodeFor(domNode as Node);
		return ruleNode ? new SchemaDom(this, ruleNode, domNode, options) : null;
	}

	fixDocForExport(doc: Document) {
		if (this.namespaces) {
			const ns = this.namespaces;
			const elt = doc.documentElement;
			for (const prefix in ns) {
				if (prefix) elt.setAttributeNS(DOM.XMLNS_NS, "xmlns:" + prefix, ns[prefix]);
				else elt.setAttributeNS(DOM.XMLNS_NS, "xmlns", ns[prefix]);
			}
		}
	}


	computeIndex() {
		if (!this._rulesIndex) {
			this._rulesIndex = [];
			this.startRule.indexPatterns(this._rulesIndex);
		}
	}

	protected _rulesIndex: Array<SkRule>;

	writeTo(o: any, mode: ESerialMode) {
		this.computeIndex();
		o.rules = Serializable.serialArraySerializables(this._rulesIndex, mode);
		if (this.unknownEltRule !== SkRuleEltUnknown.DEFAULT) o.unknownEltRule = Serializable.serialSerializable(this.unknownEltRule, mode);
		if (this.unknownAttrRule !== SkRuleAttrUnknown.DEFAULT) o.unknownAttrRule = Serializable.serialSerializable(this.unknownAttrRule, mode);
		if (this.forbiddenTextRule !== SkRuleTextForbidden.DEFAULT) o.forbiddenTextRule = Serializable.serialSerializable(this.forbiddenTextRule, mode);
		if (this.commentRule !== SkRuleComment.DEFAULT) o.commentRule = Serializable.serialSerializable(this.commentRule, mode);
	}

	readFrom(o: any, mode: ESerialMode): this {
		this._rulesIndex = Serializable.deserialArraySerializables(o.rules, mode, SkRule.registry);
		if (o.unknownEltRule) {
			this.unknownEltRule = Serializable.deserialSerializable(o.unknownEltRule, mode, SkRule.registry);
			this.unknownEltRule.linkRules(this);
		} else {
			this.unknownEltRule = SkRuleEltUnknown.DEFAULT;
		}
		if (o.unknownAttrRule) {
			this.unknownAttrRule = Serializable.deserialSerializable(o.unknownAttrRule, mode, SkRule.registry);
			this.unknownAttrRule.linkRules(this);
		} else {
			this.unknownAttrRule = SkRuleAttrUnknown.DEFAULT;
		}
		if (o.forbiddenTextRule) {
			this.forbiddenTextRule = Serializable.deserialSerializable(o.forbiddenTextRule, mode, SkRule.registry);
			this.forbiddenTextRule.linkRules(this);
		} else {
			this.forbiddenTextRule = SkRuleTextForbidden.DEFAULT;
		}
		if (o.commentRule) {
			this.commentRule = Serializable.deserialSerializable(o.commentRule, mode, SkRule.registry);
			this.commentRule.linkRules(this);
		} else {
			this.commentRule = SkRuleComment.DEFAULT;
		}
		this.startRule = this._rulesIndex[0] as SkRuleNode | SkRuleDirective;
		this.startRule.linkRules(this);
		return this;
	}

	resolveRule(rule: string | number | SkRule): SkRule {
		if (typeof rule === 'number') {
			const res = this._rulesIndex[rule];
			if (res.id === undefined) res.id = rule;
			return res;
		}
		return rule as SkRule;
	}

	createNewDoc(): IJmlSet {
		const eltRule = this.startRule.findRule((r: SkRule) => r.structType == ENodeType.document);
		const jml = [] as IJmlSet;
		const xmlRoot = eltRule.createContent(jml);
		if (jml.length === 0) jml.push({'': xmlRoot || "x"});
		const elt = jml[0] as IJmlObj;
		if (this.namespaces) for (const prefix of Object.keys(this.namespaces)) {
			if (prefix === '') {
				elt['xmlns'] = this.namespaces[prefix];
			} else {
				elt['xmlns:' + prefix] = this.namespaces[prefix];
			}
		}
		return jml;
	}

}


export interface OSchemaDomConfig {

	/** Pour éditer un fragment virtuel qui n'est pas la racine du document. */
	rootRule?: SkRuleNode;

	/** Nom de la propriété ajoutée aux noeuds Document et Element pour référencer le {SkNode}. */
	bindKey?: string | symbol;
}

/** Association d'un Schema à un noeud Dom racine. */
export class SchemaDom {
	static readonly EXEC_OPTIONS_DEFAULT: OSkExecOptions = Object.freeze({genAnnots: true});

	static DEBUG = false;

	/** Clé pour associer les SkNode aux élements du DOM (les noeuds de type attributs, text et document ne sont pas bindés aujourd'hui). */
	bindKey: string | symbol;

	/** Fournisseur personnalisé de GramTransposer. */
	gramTransposerProv?: IGramTransposerProv;

	house?: XmlTypedHouse;

	crossDomMgrs?: Dict<ISkCrossDomMgr>;

	// /** Caches de données propres à chaque ISkMetaNode (voir chaque rule de ISkMetaNode) ET pour ce document uniquement. */
	// get skmDatas(): Map<SkRule | ISkMetaNode, any> {return this._skmDatas || (this._skmDatas = new Map())}
	//
	// _skmDatas: Map<SkRule | ISkMetaNode, any>;

	/**
	 *
	 * @param schema
	 * @param startRule
	 * @param nodeRoot Généralement un Document, mais pourrait être un élément pour une analyse partielle.
	 * @param config
	 */
	constructor(public schema: Schema, startRule: SkRuleNode, public nodeRoot: Document | DocumentFragment | Element, config?: OSchemaDomConfig) {
		this.schema = schema;
		this.nodeRoot = nodeRoot;
		this.bindKey = (config && config.bindKey) || "_sk"; //Ou Symbol ? string pour debug pour le moment...
		if (schema.crossDomMgrFactories) {
			this.crossDomMgrs = {};
			const factories = schema.crossDomMgrFactories;
			for (let key in factories) this.crossDomMgrs[key] = factories[key](this);
		}
		new SkNode(startRule, nodeRoot, this);
	}

	get document(): Document {return this.nodeRoot instanceof Document ? this.nodeRoot : this.nodeRoot.ownerDocument}

	/**
	 * (Re)validation du noeud root et de toute sa descendance.
	 * Aucune correction n'est effectuée ici, cf correctDocument().
	 *
	 * @param execOptions Options d'exécution relative à la validation du schéma.
	 */
	validateDocument(execOptions?: OSkExecOptions): OSkExecResults {
		const results = this.xBuildExecResult(execOptions);
		results.resetAll = true;
		this.startValid(results);
		this.getSkNode(this.nodeRoot).execRules(results);
		this.endValid(results);
		results.resetAll = false;
		return this.xExecRevalid(results);
	}

	/**
	 * Valide et corrige le document avec les options définie : autoMutate, autoCorrect...
	 */
	correctDocument(execOptions: OSkExecOptions) {
		let cycles = 0;
		let results = this.validateDocument(Object.create(execOptions));
		while (results.mutations || results.corrections.length > 0) {
			//On execute les muatations seules avant les autres corrections.
			new XmlBodyState('').resetDom(this.document).update(new XmlListMsgOt().initConcurrentList(results.mutations || results.corrections));
			results = this.validateDocument(Object.create(execOptions));
			if (cycles++ > 50) {
				console.warn("Infinite cleanupContentToPaste loop: ", results, this.document);
				break;
			}
		}
	}

	/**
	 * Revalidation partielle de certains noeuds du document (suite à modif de leurs fils ou attributs).
	 * Liste des noeuds à revalider dans OSkExecOptions.shouldRevalid
	 */
	revalid(execOptions: OSkExecOptions): OSkExecResults {
		return this.xExecRevalid(this.xBuildExecResult(execOptions));
	}

	protected xExecRevalid(results: OSkExecInternalOptions): OSkExecInternalOptions {
		while (results.shouldRevalid) {
			results.pendingRevalid = results.shouldRevalid;
			results.shouldRevalid = null;
			for (const n of results.pendingRevalid) {
				//1ère passe,
				// 1 - on reset le skNode des noeuds dont l'association à la rule a changé (deepMatching).
				//     Permet aux validations profondes (tables, ...) d'être certain d'avoir les rules des skNodes à jour (col, row, cell connues).
				// 2 - on élimine la validation d'un noeud nouvellement inséré si son parent va aussi être (re)validé pour éviter un double autoComplete.
				if (n !== this.nodeRoot) {
					const skNode = this.getSkNode(n);
					if (skNode) {
						if (this.needRefreshParent(skNode)) {
							this.resetSkNode(n);
							//on (re)planifie la validation du père (qui validera aussi ce fils du fait de resetSkNode).
							//note : on (re)planifie car même si le père est déjà dans toRevalid, ce reset du fils pourrait changer le deepMatching du père.
							(results.shouldRevalid || (results.shouldRevalid = new Set())).add(n.parentNode as Element | Document);
						}
					} else if (this.getSkNode(n.parentNode) == null || results.pendingRevalid.has(n.parentNode as Element | Document)) {
						//Ce noeud est nouveau et le parent est aussi une insertion ou va être validé,
						//=> on élimine ce noeud car sa validation sera appliquée à partir du parent
						//Sinon : double validation et donc ajout d'un double autoComplete.
						results.pendingRevalid.delete(n);
					}
				}
			}
			this.startValid(results);
			for (const n of results.pendingRevalid) {
				const skNode = this.getSkNode(n);
				if (skNode && n.isConnected) {
					//n n'a pas été supprimé par un IXmlMsg de correction.
					skNode.execRules(results);
				}
			}
			this.endValid(results);
		}
		results.pendingRevalid = null;
		return results;
	}

	protected startValid(options: OSkExecInternalOptions) {
		if (options.searches) for (const s of options.searches) {
			if ('beforeSearchAnnots' in s) s.beforeSearchAnnots(this)
		}
		const crossMgrs = this.crossDomMgrs;
		if (crossMgrs) for (const k in crossMgrs) {
			const crossMgr = crossMgrs[k];
			if ('onStartValid' in crossMgr) crossMgr.onStartValid(options);
		}
	}

	protected endValid(options: OSkExecInternalOptions) {
		const crossMgrs = this.crossDomMgrs;
		if (crossMgrs) for (const k in crossMgrs) {
			const crossMgr = crossMgrs[k];
			if ('onEndValid' in crossMgr) crossMgr.onEndValid(options);
		}
	}

	protected needRefreshParent(skNode: SkNode): boolean {
		if (!skNode.rule.deepMatch(skNode.node as Element)) return true;
		if (skNode.rule.matcher.isFuzzy()) {
			//si on est une racine d'un contexte inconnu (ie le parent est connu), on rafraichit.
			const parent = skNode.node.parentNode;
			if (parent) {
				const skParentNode = this.getSkNode(parent);
				if (skParentNode && !skParentNode.rule.matcher.isFuzzy()) return true;
			}
		}
		return false;
	}

	getAnnots(node: Node, deep?: boolean, filter?: (a: ISkAnnot) => boolean, arrayToAppend: Array<ISkAnnot> = []): Array<ISkAnnot> {
		if (node.nodeType === Node.TEXT_NODE) {
			const skNode = this.getSkNode(node.parentNode);
			if (skNode) {
				if (skNode.annots) for (const a of skNode.annots) {
					if (a.anchorNode === node) if (!filter || filter(a)) arrayToAppend.push(a);
				}
				if (skNode.extAnnots) for (const a of skNode.extAnnots) {
					if (a.anchorNode === node) if (!filter || filter(a)) arrayToAppend.push(a);
				}
			}
		} else {
			const skNode = this.getSkNode(node);
			if (skNode) {
				if (skNode.annots) arrayToAppend.push(...(filter ? skNode.annots.filter(filter) : skNode.annots));
				if (skNode.extAnnots) arrayToAppend.push(...(filter ? skNode.extAnnots.filter(filter) : skNode.extAnnots));
				if (deep) {
					for (let ch = skNode.node.firstElementChild; ch; ch = ch.nextElementSibling) {
						this.getAnnots(ch, deep, filter, arrayToAppend);
					}
				}
			}
		}
		return arrayToAppend;
	}

	getAllAnnots(filter?: (a: ISkAnnot) => boolean): Array<ISkAnnot> {
		return this.getAnnots(this.nodeRoot, true, filter);
	}

	scanSkNodes(cb: (skNode: SkNode) => void, from?: Node) {
		if (!from) from = this.document;
		cb(this.getSkNode(from));
		const tw = this.document.createTreeWalker(from, NodeFilter.SHOW_ELEMENT);
		while (tw.nextNode()) cb(this.getSkNode(tw.currentNode));
	}

	doSearch(addSearch: ISchemaSearch | null, remSearch: ISchemaSearch | null, annotsToAdd: ISkAnnot[], annotsToRem: ISkAnnot[]) {
		if (addSearch?.beforeSearchAnnots) addSearch.beforeSearchAnnots(this);
		const filter = {
			me: this,
			acceptNode(node: Node): number {
				const skNode = (this.me as SchemaDom).getSkNode(node);
				if (skNode) {
					skNode.doSearch(addSearch, remSearch, annotsToAdd, annotsToRem);
				} else {
					console.log("No skNode for", node);
				}
				return NodeFilter.FILTER_ACCEPT;
			}
		};
		const tw = this.document.createTreeWalker(this.nodeRoot, NodeFilter.SHOW_ELEMENT, filter);
		if (addSearch) while (tw.nextNode() && annotsToAdd.length <= addSearch.maxResults) ;
		addSearch = null; //limite des ajouts atteinte
		if (remSearch) while (tw.nextNode()) ; //on poursuit pour les suppressions.
	}

	getStruct(elt: Element): SkRuleNode {
		const skNode = this.getSkNode(elt);
		return skNode ? skNode.rule : null;
	}

	getStructAtt(att: Attr): SkRuleAttr {
		const skNode = this.getSkNode(att.ownerElement);
		if (!skNode) return null;
		return skNode.rule.contentRule.findRuleAttrFor(att.nodeName);
	}

	/**
	 * Liste les types de noeuds insérables dans l'interstice spécifié.
	 * @param parentNode Document ou Element.
	 * @param gapOffset point d'insertion ou -1 pour n'importequel offset.
	 * @return Structures insérables
	 */
	listInsertableNodes(parentNode: Node, gapOffset: number | -1): ISkStructDef[] {
		const skNode = this.getSkNode(parentNode);
		if (!skNode) return null;
		const execOptions: OSkExecInternalOptions = {list1InsertableNodesAt: gapOffset, insertableNodes1: []};
		skNode.execRules(execOptions);
		return execOptions.insertableNodes1;
	}

	/** Liste les attributs insérables sur l'élément spécifié. */
	listInsertableAttrs(eltNode: Element): ISkStructDef[] {
		const execOptions: OSkExecInternalOptions = {insertableAttrs: []};
		const skNode = this.getSkNode(eltNode);
		if (!skNode) return null;
		skNode.execRules(execOptions);
		return execOptions.insertableAttrs;
	}

	/**
	 * Retourne 1, 2 ou 3 listes de structures insérables.
	 * @param parentNode Noeud parent à partir duquel rechercher les structs
	 * @param offset1 offset du 1er point d'insertion, correspondant à la 1ère liste ou -1 pour n'importequel offset
	 * 				(null si seule la liste des attributs est souhaitée).
	 * @param offset2 offset du 2ème point d'insertion, correspondant à la 2ème liste (null si la liste des attributs est souhaitée).
	 * @param attrs true si la liste des attributs insérables sur le parent est souhaitée, correspondant à la 2ème ou 3ème liste
	 */
	listInsertableStructs(parentNode: Node, offset1: number | -1 | null, offset2?: number | null, attrs?: boolean): (ISkStructDef | undefined)[][] {
		const result = [] as ISkStructDef[][];
		const skNode = this.getSkNode(parentNode);
		if (!skNode) return result;
		const execOptions: OSkExecInternalOptions = {};
		if (offset1 != null) {
			execOptions.list1InsertableNodesAt = offset1;
			execOptions.insertableNodes1 = [];
		}
		if (offset2 != null) {
			execOptions.list2InsertableNodesAt = offset2;
			execOptions.insertableNodes2 = [];
		}
		if (attrs) execOptions.insertableAttrs = [];
		skNode.execRules(execOptions);
		if (offset1 != null) result.push(execOptions.insertableNodes1);
		if (offset2 != null) result.push(execOptions.insertableNodes2);
		if (attrs) result.push(execOptions.insertableAttrs);
		return result;
	}

	/**
	 * Recherche l'offset d'insertion pour un noeud fils en fonction du schéma.
	 * Si le document contient des noeuds commentaires au point d'insertion, l'offset retourné est après le(s) commentaires(s).
	 * Pour un type attribut, retourne 0 si l'attribut n'existe pas déjà (-1 sinon, -2 si attr inconnu).
	 * @return
	 *    -1 si insertion impossible : struct déjà présente et (card=?|1 || ifExistAndCardN='reject'))
	 *    -2 si struct inconnue dans le contexte de parentNode.
	 */
	getInsertableOffset(parentNode: Node, nodeType: ENodeType, ifExistAndCardN: 'reject' | 'start' | 'end' = 'reject', nodeName?: string): number {
		const skNode = this.getSkNode(parentNode);
		if (!skNode) return -2;
		if (nodeType === 3 && ifExistAndCardN === 'reject') {
			//recherche de noeud texte, cas particulier:
			//même si le schéma est de type data et que le texte courant ne matche pas les règles,
			//on considère qu'aucun autre noeud texte ne peut être inséré, c'est le noeud texte courant qui est mauvais.
			if (DOM.findFirstChild(parentNode, DOM.IS_text) != null) return -1;
			//aucun noeud texte actuellement, on analyse le contentRule pour voir si le texte est autorisé.
		}
		const execOptions: OSkExecInternalOptions = {findOffsetNodeType: nodeType, findOffsetNodeName: nodeName, findOffsetIfExistAndCardN: ifExistAndCardN};
		skNode.execRules(execOptions);
		return "findOffset" in execOptions ? execOptions.findOffset : -2;
	}

	/**
	 * Recherche la position d'insertion possible de 'content' autour d'une séquence donnée.
	 * @return Combinaison binaire de EPastePos.
	 */
	getPastePos(parentNode: Node, offsetMin: number, offsetMax: number, content: Node | IJmlObj | SkRule): EPastePos | EPastePos {
		const skNode = this.getSkNode(parentNode);
		if (!skNode) return EPastePos.none;
		const execOptions: OSkExecInternalOptions = {pasteOffsetMin: offsetMin, pasteOffsetMax: offsetMax, importContent: content, pastePos: EPastePos.none};
		skNode.execRules(execOptions);
		return execOptions.pastePos;
	}

	/**
	 * Recherche la position d'insertion possible de 'content' en fils de parentNode.
	 */
	getImportPos(parentNode: Node, content: Node | IJmlObj | SkRule): IImportPos {
		const skNode = this.getSkNode(parentNode);
		if (!skNode) return null;
		const execOptions: OSkExecInternalOptions = {importContent: content};
		skNode.execRules(execOptions);
		if (execOptions.importPos) {
			execOptions.importPos.similarTo = content;
			return execOptions.importPos;
		}
		return null;
	}

	/**
	 * Retourne les structures du schéma susceptibles de remplacer ce noeud dans ce contexte.
	 *
	 * @param parent Contexte parent de la recherche d'alternatives.
	 * @param offset Offset d'insertion prévu des alternatives.
	 * @param nodeType Type du noeud/attribut dont on cherche les alternatives.
	 * @param nodeName Nom du noeud/attribut dont on cherche les alternatives.
	 */
	listAlternateStructs(parent: Element, offset: number, nodeType: ENodeType, nodeName: string, virtualPath?: Array<IJmlObj>): ISkStructDef[] | null {
		const skParent = this.getSkNode(parent);
		if (!skParent) return null;
		let parentRule = skParent.rule;
		if (virtualPath) for (const anc of virtualPath) {
			parentRule = parentRule.contentRule.findRule((rule) => rule.structMatch(ENodeType.element, JML.jmlNode2name(anc))) as SkRuleNode;
			if (!parentRule) return null;
		}
		const nodeRule = parentRule.contentRule.findRule((rule) => rule.structMatch(nodeType, nodeName)) as ISkRuleObj;
		let result: ISkStructDef[];
		if (nodeRule == null) {
			//recherche alternatives sur un noeud non reconnu
			if (virtualPath) return null; //mais dans un contexte virtuel, anomalie !
			result = nodeType === ENodeType.attribute ? this.listInsertableAttrs(parent) : this.listInsertableNodes(parent, offset);
		} else {
			parentRule.contentRule.findRule((choice: SkRule) => {
				if (!(choice instanceof SkRuleChoice) || choice.isStrChoice()) return false;
				//On est sur un autre choice
				const stack: SkRuleDirective[] = [];
				if (!choice.scanRules((r) => r === nodeRule, stack)) return false;
				//Ce choice contient bien notre nodeRule
				if (offset > 0 && stack.length > 0) {
					//Cas particulier si on est dans un groupe...
					let groupIdx = -1;
					for (let i = stack.length - 1; i >= 0; i--) {
						if (stack[i] instanceof SkRuleGroup) {
							groupIdx = i;
							break;
						}
					}
					if (groupIdx >= 0) {
						//On est dans un groupe, 2 options :
						const prev = (parent.childNodes.item(offset) as Element | CharacterData).previousElementSibling;
						const prevRule = this.getSkNode(prev).rule;
						const prevStack: SkRuleDirective[] = [];
						choice.scanRules((r) => r === prevRule, prevStack);
						const isPrevInSameGroup = prevStack[groupIdx] === stack[groupIdx];
						if (isPrevInSameGroup) {
							// - on est DANS le groupe, après le 1er noeud, on va restreindre les alternatives internes à notre groupe si on en trouve un après...
							return false;
						} else {
							// - on est sur le 1er noeud de notre groupe, on va proposer les options de notre choice ancêtre.
						}
					}
				}

				//On retourne les alternatives de ce choice.
				function findAlt(r: SkRule) {
					if (r instanceof SkRuleChoice) r.subRules.forEach(findAlt);
					else if (!r.findRule((r) => r === nodeRule)) {
						//alternative trouvée (pas un choice, ni nodeRule ni son container).
						if (!r.skMeta || r.skMeta.isInsertable(skParent, r as ISkRuleObj)) (result || (result = [])).push(r);
					}
				}

				choice.subRules.forEach(findAlt);
				return true;
			}, true);
		}
		return skParent.rule.skMeta.overwriteAlternates(skParent, parentRule, nodeRule, offset, nodeType, nodeName, result);
	}

	/** Retourne la cardinalité de cet élément dans son contexte parent. */
	getRealCardElt(node: Element): ECard | undefined {
		const skNode = this.getSkNode(node);
		if (!skNode) return undefined;
		const skNodeParent = this.getSkNode(node.parentNode);
		if (!skNodeParent) return skNode.rule.card;
		return skNodeParent.rule.contentRule.getRealCardSubNode(ENodeType.element, node.nodeName);
	}

	/** Retourne la cardinalité de cet attribut dans le contexte de son élément. */
	getRealCardAtt(node: Attr): ECard | undefined {
		const skNode = this.getSkNode(node);
		if (!skNode) return undefined;
		const skNodeParent = this.getSkNode(node.ownerElement);
		if (!skNodeParent) return skNode.rule.card;
		return skNodeParent.rule.contentRule.getRealCardSubNode(ENodeType.attribute, node.nodeName);
	}

	isEltRemovable(elt: Element): boolean | undefined {
		const parent = elt.parentNode;
		if (!parent) return false;
		const skNode = this.getSkNode(parent);
		if (!skNode) return undefined;
		const rule = skNode.rule.contentRule;
		if (!rule.structMatch(ENodeType.element, elt.nodeName)) return true; //elt inconnu => removable.
		return skNode.rule.contentRule.isEltRemovable(elt);
	}

	/**
	 * Evalue si le schéma impose eltName1 avant eltName2 dans les fils de parentNode.
	 * Retourne undefined si le schéma ne peut donner d'indication.
	 */
	checkSchemaOrder(parentNode: Node, eltName1: string, eltName2: string): boolean | undefined {
		const skNode = this.getSkNode(parentNode);
		if (!skNode) return undefined;
		return skNode.rule.contentRule.checkSchemaOrder(eltName1, eltName2);
	}

	exportRange(scope: OSkMCopyScope, to: Node | IJmlSet | null): Node | IJmlSet | null {
		let start = scope.start;
		const end = scope.end;
		if (scope.deletes && scope.forceSplitText) {
			//Si le start est en bordure d'un text on doit rentrer dedans pour gérer correctement le split du texte.
			if (XA.findDomLast(start, this.nodeRoot) instanceof Text) start = [...start, 0];
		}
		let forkDepth = end ? XA.findForkDepth(start, end) : start.length;
		if (forkDepth === start.length) {
			if (!end || forkDepth === end.length) {
				//les path égaux
				if (XA.isAttribute(start)) {
					if (to instanceof Node) {
						const att = XA.findDomLast(start, this.nodeRoot) as Attr;
						if (att) {
							const attsHolder = to.appendChild(to.ownerDocument.createElementNS(DOM.SCCORE_NS, SKMETA.SCATTRIBUTES_TAG));
							attsHolder.setAttributeNodeNS(att.cloneNode(false));
							if (scope.deletes) scope.deletes.add(new XmlStrMsg().init(start, null));
						}
					}
				} else {
					// => range vide.
					if (scope.deletes && scope.forceSplitText) {
						const ctn = XA.findDomContainer(start, this.nodeRoot);
						if (ctn instanceof Text) {
							const cutPoint = XA.last(start) as number;
							const str = ctn.nodeValue;
							if (cutPoint < str.length) {
								scope.deletes.add(new XmlDeleteMsg().init(start, str.length - cutPoint));
								scope.deletes.add(new XmlInsertMsg().init(XA.newBd(start).up().incrAtDepth(-1, 1).xa, [str.substring(cutPoint)]));
							}
						}
					}
				}
				return to;
			}
			//end est un sous-path de start, on remonte pour faire un export partiel du noeud start.
			if (forkDepth > 0) forkDepth--;
		}

		const commonNode = XA.findDom(start, this.nodeRoot, forkDepth);
		if (!commonNode) return to;

		if (commonNode instanceof CharacterData || commonNode instanceof Attr) {
			const fromRoot = start[forkDepth];
			const toRoot = end[forkDepth];
			if (typeof fromRoot === 'string') throw Error(`Clone fragment throw attributes not allowed: ${start}`);
			if (typeof toRoot === 'string') throw Error(`Clone fragment throw attributes not allowed: ${end}`);
			if (start.length !== forkDepth + 1) throw Error();
			const str = commonNode.nodeValue.substring(fromRoot, toRoot);
			if (to) JML.appendText(str, to);
			if (scope.deletes && (fromRoot < commonNode.nodeValue.length || toRoot > 0)) {
				if (scope.forceSplitText && commonNode instanceof Text) {
					const str = commonNode.nodeValue;
					if (fromRoot < str.length) scope.deletes.add(new XmlDeleteMsg().init(start, str.length - fromRoot));
					if (str.length > toRoot) scope.deletes.add(new XmlInsertMsg().init(XA.newBd(start).up().incrAtDepth(-1, 1).xa, [str.substring(toRoot)]));
				} else scope.deletes.add(new XmlDeleteMsg().init(start, toRoot - fromRoot));
			}
			return to;
		}

		const skNode = this.getSkNode(commonNode);
		skNode.rule.skMeta.exportNode(skNode, to, scope, forkDepth, forkDepth);
		return to;
	}

	/**
	 * Exporte un noeud avec des filtres spéciaux propres au schéma (ex: cells de tables...)
	 */
	exportNodeCustom(node: Node, filters: Jsonisable): Node {
		const skNode = this.getSkNode(node);
		if (!skNode) return node.cloneNode(true);
		return skNode.rule.skMeta.exportNodeCustom(skNode, filters);
	}

	tryPaste(context: OSkPasteContext, datas: OSkImportDatas): Promise<ISkImporter[]> {
		if (datas.originalLinks) {
			return this.tryPasteLinks(context, datas.originalLinks, datas);
		} else if (datas.originalDom || datas.originalHtml) {
			return this.tryPasteNodes(context, datas.originalDom || datas.originalHtml, datas);
		} else if (datas.originalText) {
			return this.tryPasteText(context, datas.originalText, datas);
		}
		return Promise.resolve(null);
	}

	tryPasteNodes(context: OSkPasteContext, content: Node, cache: OSkImportDatas): Promise<ISkImporter[]> {
		const ctx = SKMETA.buildSkImportCtxInternal(context, this);
		return SKMETA.ctxLastSkRule(ctx).skMeta.tryPasteNodes(ctx, content, cache);
	}

	tryPasteLinks<L>(context: OSkPasteContext, linksInfo: L, cache: OSkImportDatas): Promise<ISkImporter[]> {
		const ctx = SKMETA.buildSkImportCtxInternal(context, this);
		return SKMETA.ctxLastSkRule(ctx).skMeta.tryPasteLinks(ctx, linksInfo, cache);
	}

	tryPasteText(context: OSkPasteContext, text: string, cache: OSkImportDatas): Promise<ISkImporter[]> {
		const ctx = SKMETA.buildSkImportCtxInternal(context, this);
		return SKMETA.ctxLastSkRule(ctx).skMeta.tryPasteText(ctx, text, cache);
	}

	/** @see OSkPasteSearchContext */
	tryImport(context: OSkPasteSearchContext, datas: OSkImportDatas): Promise<ISkImporter[] | null> | null {
		const ctx = SKMETA.buildSkImportCtxInternal(context, this);
		if (datas.originalLinks) {
			return SKMETA.ctxLastSkRule(ctx).skMeta.tryImportLinks(ctx, datas.originalLinks, datas);
		} else if (datas.originalDom) {
			return SKMETA.ctxLastSkRule(ctx).skMeta.tryImportNodes(ctx, datas.originalDom, datas);
		} else if (datas.originalText) {
			console.trace("TODO: tryImportText");
			//return SKMETA.ctxLastSkRule(ctx).skMeta.tryImportText(ctx, datas.originalText, datas);
		}
		return Promise.resolve(null);
	}

	/** Les SkNode ne sont associés qu'aux Element | Document. */
	getSkNode(node: Element | Document | Node): SkNode {return (node as any)[this.bindKey]}

	protected resetSkNode(node: Node) {
		(node as any)[this.bindKey] = undefined;
	}

	/**
	 * Démarre un changement en masse dans le DOM, préparant
	 * un OSkExecResults qui pourra ensuite être passé en paramètre
	 * des méthodes onUpdatedNode() et onUpdatedAttr() pour cumuler les
	 * résultats dans cet unique objet résultat.
	 */
	startBulkUpdates(execOptions: OSkExecOptions): OSkExecResults {
		return this.xBuildExecResult(execOptions);
	}

	protected xBuildExecResult(execOptions?: OSkExecOptions): OSkExecInternalOptions {
		if (isSkExecInternalOptions(execOptions)) return execOptions;
		const execOpt: OSkExecInternalOptions = Object.create(execOptions || SchemaDom.EXEC_OPTIONS_DEFAULT);
		if (execOpt.genAnnots) {
			execOpt.annotsToAdd = [];
			execOpt.annotsToRemove = [];
			if (execOpt.searches) {
				execOpt.searchesCount = new Map();
				for (const s of execOpt.searches) execOpt.searchesCount.set(s, 0);
			}
		}
		if (execOpt.autoComplete || execOpt.autoCleanup || execOpt.autoNormXml || execOpt.autoNormChars) execOpt.corrections = [];
		//on crée toujours la prop resetAll pour isSkExecInternalOptions()
		execOpt.resetAll = false;
		return execOpt;
	}

	// protected xFindSkNode(node: Node): SkNode {
	// 	let skNode = (node as any)[this.bindKey];
	// 	while (!skNode && node && node !== this.nodeRoot) {
	// 		node = node.parentNode;
	// 		skNode = (node as any)[this.bindKey];
	// 	}
	// 	return skNode;
	// }

}

/**
 * Option d'exécution des règles.
 */
export interface OSkExecOptions {

	/** Si true les annotations (ISkAnnot) seront produites et mises à jour. */
	genAnnots?: boolean

	/** Si true, produit les msg permettant d'ajouter les noeuds/valeurs manquants dans le document selon le schema. */
	autoComplete?: boolean

	/**
	 * Si true, produit les msg permettant de fusionner les noeuds textes consécutifs et de purger les déclarations
	 * de namespaces (ils sont déclarés à la volée lors de l'export et à l'enregistrement sur l'élement racine).
	 */
	autoNormXml?: boolean

	/**
	 * Si true, normalise les chaines de caractères (white-spaces en particulier).
	 * NE PAS UTILISER pendant l'édition, cela bloquerait l'ajout d'un espace en fin de paragraphe par exemple.
	 * A utiliser à l'initialisation ou avant enregistrement par exemple.
	 */
	autoNormChars?: boolean

	/**
	 * Si true, produit les IXmlMsg éliminant les noeuds, attributs et textes non reconnus du document selon le schema.
	 */
	autoCleanup?: boolean

	/**
	 * Si true, produit les IXmlMsg permettant de muter des noeuds selon les metas du schema.
	 * Important :
	 * - si activé, après chaque exécution des IXmlMsg, une nouvelle validation doit être enclenchée jusqu'à
	 * ce qu'il n'y ait plus de IXmlMsg retournés.
	 * - Si les autres corrections auto (autoComplete autoCleanup) sont activées simultanément 2 listes de modifications
	 * distinctes et concurrentes seront retournées. Les mutations devront être exécutées, les corrections abandonées,
	 * puis un nouveau cycle devra être enclenché jusqu'à l'absence de mutations et de corrections.
	 */
	autoMutate?: boolean

	/** Si exécution dans le cadre d'un import, fournit ce contexte d'import. */
	importCtx?: OSkImportCtxInternal

	/** Recherche en cours pour lequel produire les annotations. genAnnots doit être à true aussi. */
	searches?: Set<ISchemaSearch>

	/** Utilisé en cas de revalidation partielle. cf SchemaDom.revalid() */
	shouldRevalid?: Set<Document | Element>
}

/**
 * Résultats de l'exécution des règles.
 */
export interface OSkExecResults extends OSkExecOptions {

	annotsToAdd?: ISkAnnot[]

	annotsToRemove?: ISkAnnot[]

	/** Corrections à appliquer si OSkExecOptions.autoComplete, autoCleanup, autoNormXml, autoNormChars sont activés. */
	corrections?: MsgOt[];

	/** Mutations à appliquer si OSkExecOptions.autoMutate est actif. null tant qu'aucune mutation n'est ajoutée. */
	mutations?: MsgOt[]

	/** Nombre de résultats de recherche trouvés par search, pour arretêr la recherche si ISchemaSearch.maxResults dépassé. */
	searchesCount?: Map<ISchemaSearch, number>
}

/** */
export interface OSkExecInternalOptions extends OSkExecResults {

	/** Utilisé par SchemaDom.validateDocument() pour forcer la revalidation de toute la descendance. */
	resetAll?: boolean

	/**  Voir SchemaDom.listInsertableNodes() */
	list1InsertableNodesAt?: number
	list2InsertableNodesAt?: number

	/** Résultats pour SchemaDom.listInsertableNodes() */
	insertableNodes1?: Array<SkRuleNode>
	insertableNodes2?: Array<SkRuleNode>

	/** Voir SchemaDom.listInsertableAttrs() */
	insertableAttrs?: Array<SkRuleAttr>

	/** Voir SchemaDom.getInsertableOffset() */
	findOffsetNodeType?: number
	findOffsetNodeName?: string
	findOffsetIfExistAndCardN?: 'reject' | 'start' | 'end'
	findOffsetInGroup?: 'start' //dans un groupe ordonné, offset à retourner dépendant de la pos relative de SkContext.currentChild et du noeud cible.
	findOffset?: number

	/** Contenu à importer en fils ou à coller autour d'une séquence pour SchemaDom.getImportPos() et SchemaDom.getPastePos(). */
	importContent?: Node | IJmlObj | SkRule
	/** Voir SchemaDom.getPastePos() */
	pasteOffsetMin?: number
	pasteOffsetMax?: number
	pastePos?: EPastePos | EPastePos
	/** Voir SchemaDom.getImportPos() */
	importPos?: IImportPos

	/** Datas spécifique à l'exécution des skMeta pour un node donné. La map doit être initialisée au 1er besoin. */
	nodeDatas?: Map<Node, any>

	/** Dans un processus de revaildation, set d'elt en cours de traitement. cf SchemaDom.revalid() */
	pendingRevalid?: Set<Document | Element>

}

function isSkExecInternalOptions(opt: OSkExecOptions): opt is OSkExecInternalOptions {
	return opt && ("resetAll" in opt);
}

/**
 * Objet accroché sur le noeud dom mémorisant l'association aux règles du schéma et les annotations
 * déjà calculées.
 */
export class SkNode {

	rule: SkRuleNode;
	node: Element | Document | DocumentFragment;
	annots?: ISkAnnot[];

	/** Annotations externes non gérées par le schema et le metaSchema. */
	extAnnots?: ISkAnnot[];

	constructor(rule: SkRuleNode, node: Element | Document | DocumentFragment, public schemaDom: SchemaDom) {
		this.rule = rule;
		this.node = node;
		(node as any)[schemaDom.bindKey] = this;
	}

	/**
	 * (re)validation de ce noeud suite à la modification de sa liste de ses fils ou de ses attributs.
	 * Lorsqu'un noeud texte est modifié c'est son élément parent qui doit être revalidé.
	 * @param execOptions Options d'exécution.
	 */
	execRules(execOptions: OSkExecInternalOptions): this {
		const skCtx = new SkContext().init(this, execOptions);
		const skMeta = this.rule.skMeta;
		if (skMeta) skMeta.onExecRulesStart(skCtx);
		this.rule.contentRule.findBestMatching(skCtx);
		skCtx.bindRulesAndExecute();
		this.rule.contentRule.execActions(skCtx);
		if (skMeta) skMeta.onExecRules(skCtx, this.rule, this.node);
		if (execOptions.genAnnots) {
			if (execOptions.searchesCount) {
				for (const [s, count] of execOptions.searchesCount.entries()) {
					if (count <= s.maxResults) {
						const annots = skCtx.newAnnots || [];
						const oldLen = annots.length;
						s.buildSearchAnnots(this, annots);
						if (annots.length > oldLen) {
							if (!skCtx.newAnnots) skCtx.newAnnots = annots;
							execOptions.searchesCount.set(s, count + annots.length - oldLen);
						}
					}
				}
			}
			SkNode.buildAnnotDiff(this.annots, skCtx.newAnnots, execOptions);
			this.annots = skCtx.newAnnots;
		}
		return this;
	}

	/** Interne : à partir d'une ancienne et d'une nouvelle liste d'annots, construit le diff des annots supprimées et celles ajoutées.  */
	static buildAnnotDiff(oldAnnots: ISkAnnot[] | null, newAnnots: ISkAnnot[] | null, result: OSkExecInternalOptions) {
		if (newAnnots) {
			if (oldAnnots) {
				newAnnots.forEach((newAnn, newIdx) => {
					//dédoublonnage : on élimine les nouvelles annots qui existaient déjà
					const idx = oldAnnots.findIndex((a: ISkAnnot) => a && newAnn.equals(a));
					if (idx < 0) result.annotsToAdd.push(newAnn);
					else {
						newAnnots[newIdx] = oldAnnots[idx]; //on reprend l'ancienne annot pour égalité ===.
						oldAnnots[idx] = null;//annot inchangée, cleanup pour onRemovedAnnot().
					}
				});
			} else {
				Array.prototype.push.apply(result.annotsToAdd, newAnnots);
			}
		}
		if (oldAnnots) {
			oldAnnots.forEach(a => {
				if (a) result.annotsToRemove.push(a);
			});
		}
	}


	doSearch(addSearch: ISchemaSearch | null, remSearch: ISchemaSearch | null, annotsToAdd: ISkAnnot[], annotsToRem: ISkAnnot[]) {
		if (remSearch && this.annots) {
			//Suppr des éventuelles précédentes annots de la search à suppr
			for (let i = 0; i < this.annots.length; i++) {
				if ((this.annots[i] as ISkSearchAnnot).search === remSearch) {
					annotsToRem.push(this.annots[i]);
					this.annots.splice(i--, 1);
				}
			}
		}
		if (addSearch) {
			//Nouvelle recherche
			const curSize = annotsToAdd.length;
			addSearch.buildSearchAnnots(this, annotsToAdd);
			if (curSize < annotsToAdd.length) {
				if (!this.annots) this.annots = [];
				for (let i = curSize; i < annotsToAdd.length; i++) this.annots.push(annotsToAdd[i]);
			}
		}
	}

	remDiff(diffSession: IXmlDiffSession, annotsToRem: ISkDiffAnnot[]) {
		if (this.extAnnots) {
			//statistiquement, toutes les foreignAnnots sont de la même session,
			let i = 0;
			for (; i < this.extAnnots.length; i++) {
				if ((this.extAnnots[i] as ISkDiffAnnot).diffSession !== diffSession) {
					//Raté des annots ne sont pas de la même session.
					if (i > 0) {
						//on suppr les 1ères passées
						for (let k = 0; k < i; k++) annotsToRem.push(this.extAnnots[k] as ISkDiffAnnot);
						this.extAnnots.splice(0, i);
						i = 0;
					}
					break;
				}
			}
			if (i < this.extAnnots.length) {
				//foreignAnnots contient d'autres annots que celles de cette session.
				for (; i < this.extAnnots.length; i++) {
					if ((this.extAnnots[i] as ISkDiffAnnot).diffSession === diffSession) {
						annotsToRem.push(this.extAnnots[i] as ISkDiffAnnot);
						this.extAnnots.splice(i--, 1);
					}
				}
			} else {
				//Toutes les foreignAnnots sont de cette session.
				annotsToRem.push(...this.extAnnots as ISkDiffAnnot[]);
				this.extAnnots = null;
			}
		}
	}
}

/**
 * Contexte d'un processus d'exécution des règles sur un skNode donné.
 */
export class SkContext extends Map<Node | Attr, ISkRuleObj> {

	skNode: SkNode;
	execOptions: OSkExecInternalOptions;

	/** Annotations créées. */
	newAnnots: Array<ISkAnnot>;

	protected unknownsCh: Array<Node | SkRuleNode>;

	/** Pile mémoire d'états lors de l'analyse des rules. */
	protected states: Array<any> = [];
	/** Offset pour implémentation virtuelle du shift (perfs dramatiques sur Firefox). */
	protected statesIdx: number = 0;

	/** Noeud courant du contexte. */
	protected _currCh: Node;

	/** List de strings en cours d'analyse (issu d'un attribut ou d'un noeud texte). */
	//protected _text: string[];
	//protected _textIdx: number;

	init(skNode: SkNode, execOptions: OSkExecInternalOptions): this {
		this.skNode = skNode;
		this.execOptions = execOptions;
		this._currCh = this.xGetValidChild(skNode.node.firstChild);
		return this;
	}

	protected xGetValidChild(ch: Node): Node {
		while (ch && ch.nodeType === Node.COMMENT_NODE) {
			const rule = this.skNode.rule.commentRule || this.skNode.schemaDom.schema.commentRule;
			rule?.skMeta?.onExecRules(this, rule as SkRuleComment, ch);
			ch = ch.nextSibling;
		}
		return ch;
	}

	/** Elément container de skCtx. */
	get node(): Element | Document | DocumentFragment {
		return this.skNode.node;
	}

	get currentChild(): Node {
		return this._currCh;
	}

	set currentChild(ch: Node) {
		ch = this.xGetValidChild(ch);
		if (this.unknownsCh) {
			//On exclut les noeuds flagés unknown.
			while (ch && this.unknownsCh.indexOf(ch) >= 0) ch = this.xGetValidChild(ch.nextSibling);
		}
		this._currCh = ch;
	}

	/** Offset du noeud courant. */
	get currentOffset() {
		if (!this._currCh) return this.node.childNodes.length;
		let offset = 0;
		for (let previous = this._currCh.previousSibling; previous !== null; previous = previous.previousSibling) offset++;
		return offset;
	}

	/** Offset du noeud courant en remontant tous les commentaires précédents. */
	get currentOffsetMin() {
		let node = this._currCh ? this._currCh.previousSibling : this.node.lastChild;
		if (!node) return 0;
		while (node && node.nodeType === ENodeType.comment) node = node.previousSibling;
		return DOM.computeOffset(node, -1) + 1;
	}

	/** Offset du noeud courant en sautant tous les commentaires suivant. */
	get currentOffsetMax() {
		return this.offsetMaxOf(this._currCh);
	}

	/** Retourne la position max après 'count' noeuds suivants valides. */
	getOffsetMaxAfterCurrent(count: number) {
		let next = this._currCh;
		for (let i = 0; i < count; i++) next = this.xGetValidChild(next.nextSibling);
		return this.offsetMaxOf(next);
	}

	protected offsetMaxOf(from: Node) {
		let node = from ? from.nextSibling : null;
		if (!node) return DOM.computeOffset(from, this.node.childNodes.length);
		while (node && node.nodeType === ENodeType.comment) node = node.nextSibling;
		return DOM.computeOffset(node, this.node.childNodes.length) - 1;
	}

	/**
	 * Parcours tous les nodes fils à traiter dans ce skCtx.
	 * Si la cb retourne true, le parcours est interrompu.
	 */
	forEachChild(cb: (ch: Node) => true | void, cbThis?: Object) {
		for (let ch = this._currCh; ch; ch = this.xGetValidChild(ch.nextSibling)) {
			if (cb.call(cbThis, ch) === true) return;
		}
	}

	/**
	 * Parcours tous les nodes fils à traiter dans ce skCtx (qui n'ont pas été flagés inconnus).
	 * Si la cb retourne true, le parcours est interrompu.
	 */
	forEachBindableChild(cb: (ch: Node) => true | void, cbThis?: Object) {
		let idxUnknown = 0, nextUnknown;
		if (this.unknownsCh) nextUnknown = this.unknownsCh[idxUnknown++];
		for (let ch = this._currCh; ch; ch = this.xGetValidChild(ch.nextSibling)) {
			if (ch === nextUnknown) {
				nextUnknown = this.unknownsCh[idxUnknown++];
			} else {
				if (cb.call(cbThis, ch) === true) return;
			}
		}
	}

	/**
	 * Parcours tous les attributs de l'élément container de ce skCtx.
	 * Si la cb retourne true, le parcours est interrompu.
	 */
	forEachAttr(cb: (att: Attr) => true | void, cbThis?: Object) {
		const atts = (this.node as Element).attributes;
		if (atts) for (let i = 0, s = atts.length; i < s; i++) {
			const att = atts.item(i);
			//if (!att.nodeName.startsWith("xmlns")) {
			if (cb.call(cbThis, att) === true) return;
			//}
		}
	}

	/**
	 * Parcours tous les attributs qui ne sont pas encore bindés dans ce skCtx.
	 * Si la cb retourne true, le parcours est inetrrompu.
	 */
	forEachBindableAttr(cb: (att: Attr) => true | void, cbThis?: Object) {
		const atts = (this.node as Element).attributes;
		if (atts) for (let i = 0, s = atts.length; i < s; i++) {
			const att = atts.item(i);
			if (/*!att.nodeName.startsWith("xmlns") && */!this.has(att)) if (cb.call(cbThis, att) === true) return;
		}
	}

	/**
	 * Déclare une association d'un noeud fils à une rule et progresse dans l'avancement
	 * nouvellement associé.
	 */
	bindNodeToRule(node: Node, rule: SkRuleNode) {
		this.set(node, rule);
		this.currentChild = node.nextSibling;
	}

	/**
	 * Déclare un noeud inconnu et reset le contexte pour recommencer l'analyse avec ce noeud exclut.
	 * @param node
	 * @param rule
	 * @param preserveState offset de la pile des states à préserver.
	 */
	bindUnknownNodeToRuleAndReset(node: Node, rule: SkRuleNode, preserveState?: number) {
		if (rule == null) {
			console.log("null rule for " + DOM.ser(node));
		}
		if (!this.unknownsCh) this.unknownsCh = [rule, node];
		else this.unknownsCh.push(rule, node);
		this.clear();
		this.currentChild = this.node.firstChild;
		this.states.length = preserveState >= 0 ? preserveState + 1 : 0;
	}

	/**
	 * Déclare une association d'un attribut à une rule, que l'attribut soit connu ou non.
	 */
	bindAttrToRule(attr: Attr, rule: SkRuleAttr) {
		this.set(attr, rule);
	}

	/** Mémoire des états pour le cycle execActions. */
	pushRuleState(state: any) {
		this.states.push(state);
	}

	reserveRuleState(): number {
		this.states.push(null);
		return this.states.length - 1;
	}

	setRuleState(stateIdx: number, state: any) {
		this.states[stateIdx] = state;
	}

	getRuleStateIndex(): number {
		return this.states.length;
	}

	/** Cycle execActions : dépile les états mémorisés. */
	shiftRuleState(): any {
		return this.states[this.statesIdx++];
	}

	/** Cycle execActions : ajoute une annotation. */
	addAnnot(annot: ISkAnnot) {
		if (!this.newAnnots) this.newAnnots = [annot];
		else this.newAnnots.push(annot);
	}

	// /**
	//  * Chaine de caractère courante à valider.
	//  */
	// get currentText() {return this._text[this._textIdx]}
	//
	// setTextList(txt: string[]) {this._text = txt; this._textIdx = 0;}

	/**
	 *
	 */
	bindRulesAndExecute() {
		if (this.unknownsCh) {
			for (let i = 0, s = this.unknownsCh.length; i < s;) {
				this.xBindAndExec(this.unknownsCh[i++] as SkRuleNode, this.unknownsCh[i++] as Node);
			}
		}
		this.forEach(this.xBindAndExec, this);
		//
		this.currentChild = this.node.firstChild;
	}

	xBindAndExec(rule: ISkRuleObj, nodeOrAttr: Node | Attr) {
		if (nodeOrAttr.nodeType === Node.ELEMENT_NODE) {
			//Sur un elt, on mémorise le lien rule - node via le skNode.
			let skNode = this.skNode.schemaDom.getSkNode(nodeOrAttr);
			if (!skNode) {
				//nouveau noeud : traitement récursif
				skNode = new SkNode(rule as SkRuleNode, nodeOrAttr as Element, this.skNode.schemaDom);
				skNode.execRules(this.execOptions);
			} else if (skNode.rule !== rule) {
				//le noeud avait déjà été analysé, mais la rule associé à changée : traitement récursif
				skNode.rule = rule as SkRuleNode;
				skNode.execRules(this.execOptions);
			} else if (this.execOptions.resetAll) {
				//pas de chgt, mais demande explicite de reconstruction complète
				skNode.execRules(this.execOptions);
			}
			//execution des règles de validation de la rule sur le noeud ( (re)construction des annots).
			(rule as SkRuleNode).execActionsOnBinding(this, nodeOrAttr as Element);
		} else if (rule instanceof SkRuleAttr) {
			//execution des règles de validation de la rule sur le noeud (construction des annots).
			rule.execActionsOnBinding(this, nodeOrAttr as Attr);
			if (rule.skMeta) rule.skMeta.onExecRules(this, rule, nodeOrAttr);
		} else {
			//document, text, comment
			//execution des règles de validation de la rule sur le noeud (construction des annots).
			rule.execActionsOnBinding(this, nodeOrAttr);
			if (rule.skMeta) rule.skMeta.onExecRules(this, rule, nodeOrAttr);
		}
	}
}


export type ECard = '1' | '?' | '+' | '*';

/**
 * Utilitaires pour les cardinalités 1, ?, +, *.
 */
export namespace CARD {

	export function isAtLeastOne(card: ECard): boolean {
		return card === '1' || card === '+'
	}

	export function isOptionnal(card: ECard): boolean {
		return card === '?' || card === '*'
	}

	export function isRequired(card: ECard): boolean {
		return card === '1' || card === '+'
	}

	export function isRepeatable(card: ECard): boolean {
		return card === '+' || card === '*'
	}

	/**
	 * Combinaison des cardinalités entre la directive parent et une rule fille :
	 *     | 1 ? + *
	 * |---|--------
	 * | 1 | 1 ? + *
	 * | ? | ? ? * *
	 * | + | + * + *
	 * | * | * * * *
	 */
	export function combineCard(c1: ECard, c2: ECard): ECard {
		if (c1 === '1') return c2;
		if (c2 === '1') return c1;
		if (c1 === '*' || c2 === '*') return '*';
		if (c1 === '?') return c2 === '+' ? '*' : '?';
		return c2 === '?' ? '*' : '+'; //c1=='+'
	}
}

/** Position d'insertion d'un "Coller ici". */
export const enum EPastePos {
	none = 0,
	/** Insertion avant le 1er noeud de la zone d'insertion. */
	before = 1,
	/**
	 * Insertion dans un interstice ou remplacement du noeud représentant le point d'insertion.
	 * (utilisé aussi si le noeud est virtuel)
	 */
	replace = 2,
	/** Insertion après le dernier noeud de la zone d'insertion. */
	after = 4,
	/** Cas fréquent d'import dans un set (choice en card multi). */
	anywhere = before | replace | after
}

/** Position d'insertion d'un "Coller quelque-part". */
export interface IImportPos {
	/** Première position d'insertion autorisée (incluse). */
	insertOffsetMin?: number
	/** Dernière position d'insertion autorisée (incluse). */
	insertOffsetMax?: number
	/** Nom du ou des attributs susceptibles d'être insérés. */
	insertAtts?: string | string[]
	/** Cas particulier des remplacements (liens) : offset des fils ou attributs susceptibles d'être remplacés. */
	replaceChildren?: (number | string)[]
	/** Définition du contenu importable pour correctement gérer l'insertion graphique. */
	similarTo?: IJmlObj | ISkStructDef | Node | Attr
}


export interface IGramTransposerProv {

	/**
	 * Retourne tous les transposers susceptibles de traiter ce gram.
	 * Les premiers transposers retournés sont à provilégier sur les suivants (si scores équivalents).
	 */
	transposersFor(gram: IGram): IterableIterator<IGramTransposer>

	addTransposer(forGram: any/*typeof GramS.constructor*/, trsp: IGramTransposer): void
}
